import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ToastProvider } from './components/common/ToastProvider';
import AppLoadingOverlay from './components/common/AppLoadingOverlay';
import MuiAppProvider from './components/common/MuiAppProvider';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import LoginPage from './pages/LoginPage';
import ErrorBoundary from './components/common/ErrorBoundary';
import {
  SignUpPage,
  HomePage,
  EmployeesPage,
  RecruitmentPage,
  AttendancePage,
  WorkSchedulesPage,
  EmployeeRequestsPage,
  AdminRequestsPage,
  PayrollPage,
  KpiPage,
  ViolationsPage,
  SalaryComponentsPage,
  SalaryProfilesPage,
  RemindersPage,
  QrAttendanceScannerPage,
  MonthlyReportPage,
  BusinessTripReportPage,
  DailyJournalPage,
  AdminDailyJournalPage,
  InterviewPage,
  TrainingPage,
  TrainingCatalogPage,
  CourseViewPage,
  MemoPage,
  CalendarPage,
  SOPManagementPage,
  SettingsPage,
  WigManagementPage,
  PermitRequestFormPage,
  LeaveRequestFormPage,
  SickLeaveRequestFormPage,
  CourseEditorPage,
  AssetManagementPage,
  OnboardingOffboardingPage,
  AcceptInvitationPage,
  PublicPsikotesPage,
  MyPlanningPage,
  BriefingHarianPage,
  LogbookTeknisiPage,
  ShiftHandoverPage,
  IspOperationsDashboardPage,
  CoordinatorDailyPage,
  StandbyOnCallPage,
  KelolaTiketPage,
  PerintahTiketPage,
  JointerStatusPage,
  HelpdeskShiftAttendancePage,
  ComplaintsPage,
  AdminComplaintsPage,
  DiscTestPage,
  DiscResultsPage,
  DiscResultDetailPage,
  DiscPrintPage,
  DiscScanPage,
  PapiTestPage,
  PapiResultsPage,
  PapiResultDetailPage,
  PapiPrintPage,
  PapiScanPage,
  TrainingApprovalsPage,
  CompetencyAssessmentPage,
  KasbonRequestFormPage,
  FinanceKasbonPage,
  MaterialRequestPage,
  ProcurementMaterialRequestPage,
  OvertimeRequestFormPage,
  LateRequestFormPage,
  EarlyLeaveRequestFormPage,
  PIPsPage,
  MediationsPage,
  UserManagementPage,
  EmployeeKpiScoreboardPage,
  HeadKpiTeamPage,
  JobCandidatesPage,
  ProfilePage,
  PendingRegistrations,
  MainLayout,
} from './pages/lazyPages';
import {
  HRD_POSITIONS,
  HEAD_POSITIONS,
  INTERN_POSITIONS,
  GA_POSITIONS,
  FINANCE_POSITIONS,
  PROCUREMENT_POSITIONS,
  OPERASIONAL_POSITIONS,
  positionMatches,
  jabatanMatches,
} from './utils/positionAccess';

const ProtectedRoute = ({
  children,
  allowedRoles,
  allowedPositions,
  allowedJabatanPositions,
  requireAll = false,
}) => {
  const { currentUser, userRole, employeeDetails, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm font-semibold text-charcoal-500">
        Memuat sesi...
      </div>
    );
  }

  if (!currentUser) return <Navigate to="/login" replace />;

  const roleAllowed = !allowedRoles || allowedRoles.includes(userRole);
  const hasPositionList = Boolean(allowedPositions?.length);
  const hasJabatanList = Boolean(allowedJabatanPositions?.length);
  const positionAllowed =
    (!hasPositionList && !hasJabatanList) ||
    (hasPositionList && positionMatches(employeeDetails, allowedPositions)) ||
    (hasJabatanList && jabatanMatches(employeeDetails, allowedJabatanPositions));

  if (requireAll) {
    if (!roleAllowed || !positionAllowed) return <Navigate to="/" />;
  } else if (!roleAllowed && !positionAllowed) {
    // Redirect to a "not authorized" page or the home page
    return <Navigate to="/" />;
  }

  return children;
};

const RequireAuth = () => {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  if (!currentUser) return <Navigate to="/login" replace />;
  return <Outlet />;
};

// Komponen utama aplikasi yang mendefinisikan semua rute
function App() {
  // Setelah Firebase siap, render aplikasi Anda seperti biasa.
  return (
    <Router>
      <AppLoadingOverlay />
      <Suspense fallback={<div className="hrd-boot" style={{ position: 'relative', minHeight: '60vh' }} />}>
      <Routes>
        {/* Rute publik yang bisa diakses tanpa login */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/psikotes" element={<PublicPsikotesPage />} />
        <Route path="/psikotes/*" element={<PublicPsikotesPage />} />
        <Route path="/qr-scan-attendance" element={<QrAttendanceScannerPage />} />

        {/* Rute yang dilindungi, memerlukan login */}
        <Route element={<RequireAuth />}>
        <Route element={<MainLayout />}>
          <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          <Route path="/my-planning" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><MyPlanningPage /></ProtectedRoute>} />
          <Route path="/sop-management" element={<ProtectedRoute><SOPManagementPage /></ProtectedRoute>} />
          <Route path="/employees" element={<ProtectedRoute><EmployeesPage /></ProtectedRoute>} />
          <Route path="/recruitment" element={<ProtectedRoute><RecruitmentPage /></ProtectedRoute>} />
          <Route path="/recruitment/interview" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><InterviewPage /></ProtectedRoute>} />
          <Route path="/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
          <Route path="/work-schedules" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><WorkSchedulesPage /></ProtectedRoute>} />
          <Route path="/jointer/status" element={<ProtectedRoute allowedRoles={['admin', 'karyawan']} allowedPositions={['jointer']}><JointerStatusPage /></ProtectedRoute>} />
          <Route path="/helpdesk/attendance" element={<ProtectedRoute allowedRoles={['admin', 'karyawan', 'helpdesk']} allowedPositions={['helpdesk']}><HelpdeskShiftAttendancePage /></ProtectedRoute>} />
          <Route path="/requests" element={<ProtectedRoute><EmployeeRequestsPage /></ProtectedRoute>} /> {/* Updated route */}
          <Route path="/complaints" element={<ProtectedRoute allowedRoles={['admin', 'karyawan']}><ComplaintsPage /></ProtectedRoute>} />
          <Route
            path="/material-requests"
            element={
              <ProtectedRoute
                allowedRoles={['admin', 'hr', 'hrd', 'HRD']}
                allowedPositions={[...HRD_POSITIONS, ...OPERASIONAL_POSITIONS]}
              >
                <MaterialRequestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/material-requests"
            element={
              <ProtectedRoute
                allowedRoles={['admin', 'hr', 'hrd', 'HRD']}
                allowedPositions={HRD_POSITIONS}
                allowedJabatanPositions={PROCUREMENT_POSITIONS}
              >
                <ProcurementMaterialRequestPage />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/requests" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={[...HRD_POSITIONS, ...HEAD_POSITIONS]}><AdminRequestsPage /></ProtectedRoute>} />
          <Route path="/admin/kasbon" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS} allowedJabatanPositions={FINANCE_POSITIONS}><FinanceKasbonPage /></ProtectedRoute>} />
          <Route path="/admin/complaints" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={[...HRD_POSITIONS, ...GA_POSITIONS]}><AdminComplaintsPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute allowedRoles={['admin']}><SettingsPage /></ProtectedRoute>} />
          <Route path="/assets" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={[...HRD_POSITIONS, ...GA_POSITIONS]}><MuiAppProvider><AssetManagementPage /></MuiAppProvider></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><MuiAppProvider><OnboardingOffboardingPage /></MuiAppProvider></ProtectedRoute>} />
          <Route path="/payroll" element={<ProtectedRoute><PayrollPage /></ProtectedRoute>} />
          <Route path="/kpi" element={
            <ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}>
              <KpiPage />
            </ProtectedRoute>
          } />
          <Route path="/kpi/team" element={
            <ProtectedRoute allowedPositions={HEAD_POSITIONS}>
              <HeadKpiTeamPage />
            </ProtectedRoute>
          } />
          <Route path="/violations" element={<ProtectedRoute><ViolationsPage /></ProtectedRoute>} />
          <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
          <Route path="/salary-components" element={<ProtectedRoute><SalaryComponentsPage /></ProtectedRoute>} />
          {/* ISP Operations Routes */}
          <Route path="/isp-operations" element={<ProtectedRoute allowedRoles={['admin']}><IspOperationsDashboardPage /></ProtectedRoute>} />
          <Route path="/isp/coordinator" element={<ProtectedRoute allowedRoles={['admin', 'koordinator teknisi']} allowedPositions={['head dept', 'head department', 'koordinator teknisi', 'koordinator']}><CoordinatorDailyPage /></ProtectedRoute>} />
          <Route path="/isp/briefing" element={<ProtectedRoute allowedRoles={['admin', 'koordinator teknisi']} allowedPositions={['head dept', 'head department', 'koordinator teknisi']}><BriefingHarianPage /></ProtectedRoute>} />
          <Route path="/isp/logbook" element={<ProtectedRoute allowedRoles={['admin', 'teknisi', 'noc']} allowedPositions={['teknisi', 'noc']}><LogbookTeknisiPage /></ProtectedRoute>} />
          <Route path="/isp/handover" element={<ProtectedRoute allowedRoles={['admin', 'noc', 'helpdesk']} allowedPositions={['helpdesk']}><ShiftHandoverPage /></ProtectedRoute>} />
          <Route path="/isp/tickets" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD', 'helpdesk']} allowedPositions={[...HRD_POSITIONS, 'helpdesk']}><KelolaTiketPage /></ProtectedRoute>} />
          <Route path="/isp/my-tickets" element={<ProtectedRoute allowedRoles={['admin', 'teknisi', 'koordinator teknisi', 'noc']} allowedPositions={['teknisi', 'koordinator', 'noc']}><PerintahTiketPage /></ProtectedRoute>} />
          <Route path="/isp/standby" element={<ProtectedRoute allowedRoles={['admin', 'noc', 'helpdesk']}><StandbyOnCallPage /></ProtectedRoute>} />
          <Route path="/salary-profiles" element={<ProtectedRoute><SalaryProfilesPage /></ProtectedRoute>} />
          <Route path="/monthly-report" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><MonthlyReportPage /></ProtectedRoute>} />
          <Route path="/business-trip/activity" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><BusinessTripReportPage type="activity" /></ProtectedRoute>} />
          <Route path="/business-trip/funds" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><BusinessTripReportPage type="fund" /></ProtectedRoute>} />
          <Route path="/daily-journal" element={<ProtectedRoute allowedRoles={['karyawan', 'magang', 'pkl', 'intern']} allowedPositions={INTERN_POSITIONS} requireAll><DailyJournalPage /></ProtectedRoute>} />
          <Route path="/admin/daily-journal" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><AdminDailyJournalPage /></ProtectedRoute>} />
          <Route path="/training" element={<ProtectedRoute><TrainingCatalogPage /></ProtectedRoute>} />
          <Route path="/training/course/:courseId" element={<ProtectedRoute><CourseViewPage /></ProtectedRoute>} />
          <Route path="/training/admin" element={<ProtectedRoute allowedRoles={['admin','hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><TrainingPage /></ProtectedRoute>} />
          <Route path="/training/competency" element={<ProtectedRoute allowedRoles={['admin','hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><CompetencyAssessmentPage /></ProtectedRoute>} />
          <Route path="/course-editor/:courseId" element={<ProtectedRoute allowedRoles={['admin','hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><CourseEditorPage /></ProtectedRoute>} />
          <Route path="/training-approvals" element={<ProtectedRoute><TrainingApprovalsPage /></ProtectedRoute>} />
          <Route path="/memo" element={<ProtectedRoute><MemoPage /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
          <Route path="/pips" element={<ProtectedRoute><PIPsPage /></ProtectedRoute>} />
          <Route path="/mediations" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><MediationsPage /></ProtectedRoute>} />
          <Route path="/wig-management" element={<ProtectedRoute><WigManagementPage /></ProtectedRoute>} />
          {/* New request forms */}
          <Route path="/request/permit" element={<ProtectedRoute allowedRoles={['karyawan']}><PermitRequestFormPage /></ProtectedRoute>} />
          <Route path="/request/leave" element={<ProtectedRoute allowedRoles={['karyawan']}><LeaveRequestFormPage /></ProtectedRoute>} />
          <Route path="/request/sick" element={<ProtectedRoute allowedRoles={['karyawan']}><SickLeaveRequestFormPage /></ProtectedRoute>} />
          <Route path="/request/late" element={<ProtectedRoute allowedRoles={['karyawan']}><LateRequestFormPage /></ProtectedRoute>} />
          <Route path="/request/early-leave" element={<ProtectedRoute allowedRoles={['karyawan']}><EarlyLeaveRequestFormPage /></ProtectedRoute>} />
          <Route path="/request/overtime" element={<ProtectedRoute allowedRoles={['karyawan']}><OvertimeRequestFormPage /></ProtectedRoute>} />
          <Route path="/users" element={
            <ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}>
              <MuiAppProvider>
                <UserManagementPage />
              </MuiAppProvider>
            </ProtectedRoute>
          } />
          <Route path="/kpi/me" element={
            <ProtectedRoute>
              <EmployeeKpiScoreboardPage />
            </ProtectedRoute>
          } />
          <Route path="/pending-registrations" element={
            <ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}>
              <PendingRegistrations companyId={null} />
            </ProtectedRoute>
          } />
          <Route path="/request/kasbon" element={
            <ProtectedRoute allowedRoles={['karyawan', 'admin', 'hr', 'hrd', 'HRD']}>
              <KasbonRequestFormPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/recruitment/job/:jobId/candidates" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><JobCandidatesPage /></ProtectedRoute>} />
          <Route path="/disc" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><DiscTestPage /></ProtectedRoute>} />
          <Route path="/disc/results" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><DiscResultsPage /></ProtectedRoute>} />
          <Route path="/disc/results/:id" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><DiscResultDetailPage /></ProtectedRoute>} />
          <Route path="/disc/print" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><DiscPrintPage /></ProtectedRoute>} />
          <Route path="/disc/scan" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><DiscScanPage /></ProtectedRoute>} />
          <Route path="/papi" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><PapiTestPage /></ProtectedRoute>} />
          <Route path="/papi/results" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><PapiResultsPage /></ProtectedRoute>} />
          <Route path="/papi/results/:id" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><PapiResultDetailPage /></ProtectedRoute>} />
          <Route path="/papi/print" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><PapiPrintPage /></ProtectedRoute>} />
          <Route path="/papi/scan" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'hrd', 'HRD']} allowedPositions={HRD_POSITIONS}><PapiScanPage /></ProtectedRoute>} />
        </Route>
        </Route>
        <Route path="/ajukan-kasbon" element={<Navigate to="/request/kasbon" replace />} />


        {/* Rute default jika URL tidak cocok, akan diarahkan ke dashboard */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
    </Router>
  );
}

const AppWrapper = () => (
  <ErrorBoundary>
    <ToastProvider>
      <AuthProvider>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </AuthProvider>
    </ToastProvider>
  </ErrorBoundary>
);

export default AppWrapper;
